import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FaMicrosoft, FaAws } from 'react-icons/fa';
import { SiApachespark } from 'react-icons/si';

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  // Projects from your resume
  const projects = [
    {
      title: "NYCCFB - Data Quality & Modernization",
      description: "Modernized data ingestion and transformation processes by replacing legacy technologies with Microsoft Fabric. Implemented real-time data processing using SQL CDC and Fabric Eventstream for seamless analytics.",
      tags: ["Azure SQL Server", "Microsoft Fabric", "PySpark", "Delta Lake", "Eventstream", "SQL CDC"],
      tech: <FaMicrosoft />,
      color: "#0078D4"
    },
    {
      title: "Internal Analytics Platform",
      description: "Built a scalable data infrastructure using AWS services, Airbyte, Airflow, and Superset. Optimized PySpark jobs and Airflow DAGs to reduce data processing time by 30%.",
      tags: ["AWS", "PySpark", "Airbyte", "Airflow", "Superset", "EC2", "Glue", "Athena"],
      tech: <FaAws />,
      color: "#FF9900"
    },
    {
      title: "Data Quality Framework",
      description: "Contributed to the development of a comprehensive Data Quality Framework using Fabric Pipelines and PySpark, creating a one-stop solution for all data profiling needs.",
      tags: ["PySpark", "Microsoft Fabric", "Data Quality", "CI/CD"],
      tech: <SiApachespark />,
      color: "#E25A1C"
    },
    {
      title: "Real-time Data Streaming",
      description: "Implemented real-time data processing using SQL CDC in Azure SQL Server, Fabric Eventstream, and Spark Streaming for seamless data integration and analytics.",
      tags: ["Spark Streaming", "Azure SQL", "Fabric Eventstream", "Delta Lake"],
      tech: <SiApachespark />,
      color: "#E25A1C"
    }
  ];

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <ProjectsSection id="projects" ref={ref}>
      <div className="container">
        <SectionHeader
          variants={fadeUpVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          transition={{ duration: 0.6 }}
        >
          <h2>Projects</h2>
          <Divider />
        </SectionHeader>

        <ProjectsGrid>
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              variants={fadeUpVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              <ProjectHeader>
                <ProjectTitle>{project.title}</ProjectTitle>
                <TechIcon color={project.color}>{project.tech}</TechIcon>
              </ProjectHeader>
              <ProjectContent>
                <ProjectDescription>{project.description}</ProjectDescription>
                <TagsContainer>
                  {project.tags.map((tag, tagIndex) => (
                    <Tag key={tagIndex}>{tag}</Tag>
                  ))}
                </TagsContainer>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>

        <CTAContainer
          variants={fadeUpVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          transition={{ duration: 0.6, delay: 0.2 + projects.length * 0.1 }}
        >
          <p>Interested in my work?</p>
          <ContactButton href="#contact">Contact Me</ContactButton>
        </CTAContainer>
      </div>
    </ProjectsSection>
  );
};

// Styled Components
const ProjectsSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
    }
`;

const SectionHeader = styled(motion.div)`
    margin-bottom: 60px;
    text-align: center;

    h2 {
        font-size: 2.5rem;
        color: var(--text-light);
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
`;

const Divider = styled.div`
    height: 4px;
    width: 70px;
    background-color: var(--accent);
    margin: 0 auto;
`;

const ProjectsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 30px;
    margin-bottom: 50px;
`;

const ProjectCard = styled(motion.div)`
    background-color: var(--secondary);
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 10px 30px -15px var(--shadow);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 30px -15px var(--shadow);
    }
`;

const ProjectHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 25px 0;
`;

const TechIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${props => props.color || 'rgba(80, 178, 161, 0.2)'};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
`;

const ProjectContent = styled.div`
    padding: 10px 25px 25px;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const ProjectTitle = styled.h3`
    color: var(--text-light);
    font-size: 1.4rem;
    margin-bottom: 10px;
`;

const ProjectDescription = styled.p`
    color: var(--text-secondary);
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 20px;
    flex: 1;
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: auto;
`;

const Tag = styled.span`
    padding: 6px 12px;
    background-color: rgba(30, 136, 229, 0.1);
    color: var(--accent);
    font-size: 0.8rem;
    border-radius: 30px;
    white-space: nowrap;
`;

const CTAContainer = styled(motion.div)`
    text-align: center;
    margin-top: 60px;

    p {
        color: var(--text-light);
        font-size: 1.2rem;
        margin-bottom: 20px;
    }
`;

const ContactButton = styled.a`
    display: inline-block;
    padding: 12px 30px;
    background-color: var(--accent);
    color: var(--primary);
    font-size: 1rem;
    font-weight: 600;
    border-radius: 4px;
    text-decoration: none;
    transition: all 0.3s ease;
    border: 2px solid var(--accent);

    &:hover {
        background-color: transparent;
        color: var(--accent);
    }
`;

export default Projects;
